/* The signed-in chrome: header, ticker, tabs, main column, footer.
   Everything here is presentational — the shell holds no data of its own and
   takes the ticker's stats and the active tab from its caller. */
import { useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

/* Tab order is reading order: what you did today, then what is next, then the
   numbers behind both. PLAN sits directly after LOG because the two are read
   together — you open the plan to see what to eat and LOG to record that you
   did — and because it is the only tab that looks forward. VITALS follows
   TRENDS because both are the body's numbers over time, and precedes MACROS
   because a glucose curve is read before the food that shaped it. */
export const TABS = ['LOG', 'PLAN', 'TRENDS', 'VITALS', 'MACROS', 'JOURNAL', 'SETTINGS'] as const

export type Tab = (typeof TABS)[number]

export interface ShellProps {
  user: string
  tab: Tab
  onTabChange: (tab: Tab) => void
  /** Ticker phrases, already formatted. Joined with the ✦ separator. */
  ticker: string[]
  /** Clicking the user pill opens Settings. */
  onUserClick: () => void
  children: ReactNode
}

function Ticker({ items }: { items: string[] }) {
  if (items.length === 0) return null
  const run = items.join('  ✦  ')
  return (
    // aria-hidden: the ticker is decorative repetition of numbers shown on the
    // Trends and Macros tabs, and a scrolling marquee read aloud is noise.
    //
    // That is a condition, not just a description. `app/ticker.ts` builds these
    // phrases and every figure in them is a TRENDS or MACROS tile stated again;
    // a stat added there that appears nowhere else would make this bar the only
    // place a fact is said, and this attribute would have to go.
    <div className="milo-ticker" aria-hidden="true">
      <div className="milo-ticker-track">
        <div className="milo-ticker-run">
          <span style={{ padding: '0 24px' }}>{run}</span>
        </div>
        <div className="milo-ticker-run">
          <span style={{ padding: '0 24px' }}>{run}</span>
        </div>
      </div>
    </div>
  )
}

export function Shell({
  user,
  tab,
  onTabChange,
  ticker,
  onUserClick,
  children,
}: ShellProps) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  // The tablist's single tab stop is the tab that was focused most recently, not the selected
  // one: arrows move focus without selecting, so after ArrowRight the focused tab must be the
  // one Tab/Shift+Tab returns to. `aria-selected` stays tied to `tab`, which is the point of
  // manual activation — the two can legitimately disagree.
  const selectedIndex = Math.max(0, TABS.indexOf(tab))
  const [rovingIndex, setRovingIndex] = useState(selectedIndex)
  const [lastTab, setLastTab] = useState<Tab>(tab)
  // Selecting a tab from outside the tablist (the user pill opens Settings) has to move the
  // tab stop with it, or Tab would land on a tab that is no longer selected. Adjusting state
  // during render rather than in an effect: React re-renders before committing, so no flash.
  if (tab !== lastTab) {
    setLastTab(tab)
    setRovingIndex(selectedIndex)
  }

  // WAI-ARIA tabs: the tablist is one tab stop, and Arrow/Home/End move between the tabs
  // inside it (see the roving tabIndex below). Activation is deliberately *manual* — arrows
  // move focus only, and Enter/Space selects via the button's native click. Automatic
  // activation would fire a screen's data fetch on every arrow keypress while the user is
  // still scrubbing along the row.
  function handleTabKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = TABS.length - 1
    let next: number
    switch (e.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1
        break
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = last
        break
      default:
        return
    }
    e.preventDefault()
    tabRefs.current[next]?.focus()
  }

  return (
    <div className="milo-shell">
      <div className="milo-topblock">
        <header className="milo-header">
          <div className="milo-inner milo-header-inner">
            <span className="milo-wordmark">MILO</span>
            <span className="milo-header-tag">Carry the calf</span>
            <button
              type="button"
              className="milo-userpill"
              onClick={onUserClick}
              aria-label={`Signed in as ${user}. Open settings.`}
            >
              {user} ●
            </button>
          </div>
        </header>

        <Ticker items={ticker} />

        <nav className="milo-tabs" aria-label="Sections">
          <div className="milo-inner milo-tabs-inner" role="tablist">
            {TABS.map((t, i) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={t === tab}
                aria-controls="milo-panel"
                // Roving tabIndex: exactly one tab is in the page's tab order — the one that
                // holds (or last held) focus — so Tab enters and leaves the tablist rather
                // than stepping through all five, and re-enters where the user left off.
                tabIndex={i === rovingIndex ? 0 : -1}
                ref={(el) => {
                  tabRefs.current[i] = el
                }}
                className="milo-tab"
                // One place to move the tab stop: whatever gave this tab focus — an arrow
                // key's .focus(), a click, or a screen reader — lands here.
                onFocus={() => setRovingIndex(i)}
                onClick={() => onTabChange(t)}
                onKeyDown={(e) => handleTabKeyDown(e, i)}
              >
                {t}
              </button>
            ))}
          </div>
        </nav>
      </div>

      <main className="milo-main" id="milo-panel" role="tabpanel" aria-label={tab}>
        {children}
      </main>

      <footer className="milo-footer">
        Milo ✦ milo.rayray.cloud ✦ Carry the calf daily
      </footer>
    </div>
  )
}
